/**
 * @page TournamentCreatePage
 * @description 建立新賽事（UI-2-2）
 * 表單：選擇模板 → 填入基礎設定 → 選擇盲注結構 → 建立
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type {
  CreateTournamentDto,
  TournamentTemplateDto,
  BlindStructureDto,
} from '@pokerroom/types';
import { api } from '../../lib/api.ts';
import {
  applyTournamentTemplateDefaults,
  focusTournamentNameSuffix,
} from './tournamentTemplateDefaults.ts';
import { buildDeadlineOptions, clampDeadlineToOptions } from '../../lib/lateRegistration.ts';
import { RuleEditor } from './PayoutRuleSettingsPage.tsx';
import {
  type PayoutRule,
  type TournamentRuleType,
  ruleLabels,
  validPayoutRule,
} from './payoutRule.ts';

async function fetchTemplates(): Promise<TournamentTemplateDto[]> {
  return api.get<TournamentTemplateDto[]>('/tournament/templates');
}

async function fetchBlindStructures(): Promise<BlindStructureDto[]> {
  return api.get<BlindStructureDto[]>('/tournament/blind-structures');
}

async function createTournament(dto: CreateTournamentDto): Promise<{ id: string }> {
  return api.post<{ id: string }>('/tournament', dto);
}

export default function TournamentCreatePage() {
  const { t } = useTranslation('tournament');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [overrideRule, setOverrideRule] = useState<PayoutRule | null>(null);

  const { data: templates = [] } = useQuery({ queryKey: ['templates'], queryFn: fetchTemplates });
  const { data: blindStructures = [] } = useQuery({
    queryKey: ['blind-structures'],
    queryFn: fetchBlindStructures,
  });
  const { data: venueRules } = useQuery({
    queryKey: ['venue-payout-rules'],
    queryFn: () =>
      api.get<{ rules: Array<{ type: TournamentRuleType; version: number; rule: PayoutRule }> }>(
        '/tournament/payout-rules',
      ),
  });

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<CreateTournamentDto>({
    defaultValues: {
      type: 'ELIMINATION',
      payoutType: 'STANDARD',
      buyin: '1000',
      startingChips: 10000,
      lateRegLevels: 4,
      rebuyAllowed: false,
      maxRebuy: 0,
      addonAllowed: false,
      maxAddon: 0,
      manualCountMode: false,
      gtdPrizePool: '0',
      bountyAmount: '0',
      rakePct: '10',
    },
  });

  const selectedTemplateId = watch('templateId');
  const selectedType = watch('type') as TournamentRuleType;
  const venueRule = venueRules?.rules.find((item) => item.type === selectedType);
  useEffect(() => {
    setOverrideRule(null);
  }, [selectedType]);
  const selectedBlindStructureId = watch('blindStructureId');
  const lateRegLevels = watch('lateRegLevels');
  const rebuyDeadline = watch('rebuyDeadline');
  const addonDeadline = watch('addonDeadline');
  const rebuyAllowed = watch('rebuyAllowed');
  const addonAllowed = watch('addonAllowed');
  const deadlineOptions = buildDeadlineOptions(
    blindStructures.find((structure) => structure.id === selectedBlindStructureId),
  );
  useEffect(() => {
    const maximum = deadlineOptions.length;
    if (maximum === 0) return;
    const nextLateReg = clampDeadlineToOptions(lateRegLevels, maximum);
    const nextRebuy = clampDeadlineToOptions(rebuyDeadline, maximum);
    const nextAddon = clampDeadlineToOptions(addonDeadline, maximum);
    if (nextLateReg !== undefined) setValue('lateRegLevels', nextLateReg);
    if (nextRebuy !== undefined) setValue('rebuyDeadline', nextRebuy);
    if (nextAddon !== undefined) setValue('addonDeadline', nextAddon);
  }, [addonDeadline, deadlineOptions.length, lateRegLevels, rebuyDeadline, setValue]);

  /** 套用模板預設值 */
  const applyTemplate = (templateId: string) => {
    const tmpl = templates.find((t) => t.id === templateId);
    setValue('templateId', tmpl?.id);
    setValue('timedFeeVersionId', tmpl?.timedFeeVersion?.id);
    if (!tmpl) return;
    const defaults = applyTournamentTemplateDefaults(tmpl);
    for (const [field, value] of Object.entries(defaults)) {
      (setValue as (key: string, fieldValue: unknown) => void)(field, value);
    }
    setTimeout(() => {
      const nameInput = document.querySelector<HTMLInputElement>('input[name="name"]');
      if (nameInput) focusTournamentNameSuffix(nameInput);
    }, 100);
  };

  const onSubmit = async (data: CreateTournamentDto) => {
    if (overrideRule && !validPayoutRule(overrideRule, selectedType)) {
      setError('單場分配比例無效，請檢查各層合計。');
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      const result = await createTournament({
        ...data,
        ...(overrideRule ? { payoutRuleOverride: overrideRule } : {}),
      });
      void queryClient.invalidateQueries({ queryKey: ['tournaments'] });
      navigate(`/tournament/${result.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('createFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-6 max-w-2xl mx-auto">
      <h1 className="text-2xl font-bold text-base-content mb-6">{t('create')}</h1>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <section className="rounded-xl border p-4 space-y-3">
          <h2 className="font-medium">本場獎池分配規則</h2>
          <p className="text-sm text-base-content/70">
            {ruleLabels[selectedType] ?? selectedType} 場館預設版本 {venueRule?.version ?? '載入中'}
            ；建立後保存當場快照。
          </p>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={overrideRule !== null}
              onChange={(event) =>
                setOverrideRule(
                  event.target.checked && venueRule ? structuredClone(venueRule.rule) : null,
                )
              }
            />
            覆寫本場比例
          </label>
          {overrideRule && (
            <RuleEditor type={selectedType} value={overrideRule} onChange={setOverrideRule} />
          )}
        </section>
        {/* 套用模板 */}
        <div className="bg-base-300 rounded-xl p-4 border border-base-300">
          <label className="text-sm text-base-content/70 mb-2 block">{t('applyTemplate')}</label>
          <select
            aria-label="來源模板"
            value={selectedTemplateId ?? ''}
            onChange={(e) => applyTemplate(e.target.value)}
            className="w-full bg-base-300 text-base-content rounded-lg px-3 py-2"
          >
            <option value="">{t('noTemplate')}</option>
            {templates.map((tmpl) => (
              <option key={tmpl.id} value={tmpl.id}>
                {tmpl.name}
              </option>
            ))}
          </select>
        </div>

        {/* 基本設定 */}
        <div className="bg-base-300 rounded-xl p-4 border border-base-300 space-y-4">
          <h2 className="text-base-content font-medium">{t('basicSettings')}</h2>

          <div>
            <label className="text-sm text-base-content/70 block mb-1">{t('field.name')} *</label>
            <input
              {...register('name', { required: t('nameRequired') })}
              className="w-full bg-base-300 text-base-content rounded-lg px-3 py-2 border border-base-300 focus:border-primary outline-hidden"
              placeholder={t('namePlaceholder')}
            />
            {errors.name && <p className="text-error text-xs mt-1">{errors.name.message}</p>}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-base-content/70 block mb-1">
                {t('tournamentType')}
              </label>
              <select
                {...register('type')}
                className="w-full bg-base-300 text-base-content rounded-lg px-3 py-2"
              >
                <option value="ELIMINATION">{t('type.ELIMINATION')}</option>
                <option value="TIMED">{t('type.TIMED')}</option>
                <option value="BOUNTY">{t('type.BOUNTY')}</option>
                <option value="SPECIAL">{t('type.SPECIAL')}</option>
              </select>
            </div>
            <div>
              <label className="text-sm text-base-content/70 block mb-1">{t('payoutMode')}</label>
              <select
                {...register('payoutType')}
                className="w-full bg-base-300 text-base-content rounded-lg px-3 py-2"
              >
                <option value="STANDARD">{t('payoutType.STANDARD')}</option>
                <option value="ICM">{t('payoutType.ICM')}</option>
                <option value="GTD">{t('payoutType.GTD')}</option>
                <option value="BOUNTY">{t('payoutType.BOUNTY')}</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-base-content/70 block mb-1">
                {t('field.buyin')} *
              </label>
              <input
                {...register('buyin', { required: t('buyinRequired') })}
                type="text"
                className="w-full bg-base-300 text-base-content rounded-lg px-3 py-2 border border-base-300"
                placeholder="1000"
              />
            </div>
            <div>
              <label className="text-sm text-base-content/70 block mb-1">
                {t('field.startingChips')} *
              </label>
              <input
                {...register('startingChips', { required: true, valueAsNumber: true })}
                type="number"
                className="w-full bg-base-300 text-base-content rounded-lg px-3 py-2 border border-base-300"
                placeholder="10000"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-base-content/70 block mb-1">
                {t('field.rakePct')}
              </label>
              <input
                {...register('rakePct')}
                type="text"
                className="w-full bg-base-300 text-base-content rounded-lg px-3 py-2 border border-base-300"
                placeholder="10"
              />
            </div>
            <div>
              <label className="text-sm text-base-content/70 block mb-1">
                {t('field.lateRegLevels')}
              </label>
              <select
                {...register('lateRegLevels', { valueAsNumber: true })}
                disabled={deadlineOptions.length === 0}
                className="w-full bg-base-300 text-base-content rounded-lg px-3 py-2 border border-base-300"
              >
                <option value={0}>No late registration</option>
                {deadlineOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* 盲注結構 */}
        <div className="bg-base-300 rounded-xl p-4 border border-base-300">
          <label className="text-sm text-base-content/70 block mb-2">{t('blindStructure')} *</label>
          <select
            {...register('blindStructureId', { required: t('blindStructureRequired') })}
            className="w-full bg-base-300 text-base-content rounded-lg px-3 py-2 border border-base-300"
          >
            <option value="">{t('selectBlindStructure')}</option>
            {blindStructures.map((bs) => (
              <option key={bs.id} value={bs.id}>
                {bs.name}
              </option>
            ))}
          </select>
          {errors.blindStructureId && (
            <p className="text-error text-xs mt-1">{errors.blindStructureId.message}</p>
          )}
        </div>

        {/* 手動計數模式 */}
        <div className="bg-base-300 rounded-xl p-4 border border-base-300">
          <div className="flex items-start gap-3">
            <input
              {...register('manualCountMode')}
              type="checkbox"
              id="manualCountMode"
              className="w-4 h-4 mt-1"
            />
            <div>
              <label htmlFor="manualCountMode" className="text-base-content font-medium block">
                手動計數模式
              </label>
              <p className="text-sm text-base-content/70 mt-1 max-w-prose">
                不建立玩家名單，由操作員在時鐘頁直接按 +/- 記錄人數、淘汰、Rebuy 與 Addon。
                適合只需要時鐘與人數看板的場次。建立後無法變更，且這類賽事不能報名玩家、
                不會進入結算流程，獎金池也不會自動計算。
              </p>
            </div>
          </div>
        </div>

        {/* Rebuy 設定 */}
        <div className="bg-base-300 rounded-xl p-4 border border-base-300 space-y-4">
          <div className="flex items-center gap-3">
            <input
              {...register('rebuyAllowed')}
              type="checkbox"
              id="rebuyAllowed"
              className="w-4 h-4"
            />
            <label htmlFor="rebuyAllowed" className="text-base-content font-medium">
              {t('allowRebuy')}
            </label>
          </div>
          {rebuyAllowed && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-2">
              <div>
                <label className="text-sm text-base-content/70 block mb-1">
                  {t('deadlineLevel')}
                </label>
                <select
                  {...register('rebuyDeadline', { valueAsNumber: true })}
                  disabled={deadlineOptions.length === 0}
                  className="w-full bg-base-300 text-base-content rounded-lg px-3 py-2 border border-base-300"
                >
                  {deadlineOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm text-base-content/70 block mb-1">{t('cost')}</label>
                <input
                  {...register('rebuyCost')}
                  type="text"
                  className="w-full bg-base-300 text-base-content rounded-lg px-3 py-2 border border-base-300"
                />
              </div>
              <div>
                <label className="text-sm text-base-content/70 block mb-1">{t('chipsAdded')}</label>
                <input
                  {...register('rebuyChips', { valueAsNumber: true })}
                  type="number"
                  className="w-full bg-base-300 text-base-content rounded-lg px-3 py-2 border border-base-300"
                />
              </div>
              <div>
                <label className="text-sm text-base-content/70 block mb-1">{t('maxCount')}</label>
                <input
                  {...register('maxRebuy', { valueAsNumber: true })}
                  type="number"
                  min={0}
                  className="w-full bg-base-300 text-base-content rounded-lg px-3 py-2 border border-base-300"
                />
              </div>
            </div>
          )}
        </div>

        {/* Addon 設定 */}
        <div className="bg-base-300 rounded-xl p-4 border border-base-300 space-y-4">
          <div className="flex items-center gap-3">
            <input
              {...register('addonAllowed')}
              type="checkbox"
              id="addonAllowed"
              className="w-4 h-4"
            />
            <label htmlFor="addonAllowed" className="text-base-content font-medium">
              {t('allowAddon')}
            </label>
          </div>
          {addonAllowed && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-2">
              <div>
                <label className="text-sm text-base-content/70 block mb-1">
                  {t('deadlineLevel')}
                </label>
                <select
                  {...register('addonDeadline', { valueAsNumber: true })}
                  disabled={deadlineOptions.length === 0}
                  className="w-full bg-base-300 text-base-content rounded-lg px-3 py-2 border border-base-300"
                >
                  {deadlineOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm text-base-content/70 block mb-1">{t('cost')}</label>
                <input
                  {...register('addonCost')}
                  type="text"
                  className="w-full bg-base-300 text-base-content rounded-lg px-3 py-2 border border-base-300"
                />
              </div>
              <div>
                <label className="text-sm text-base-content/70 block mb-1">{t('chipsAdded')}</label>
                <input
                  {...register('addonChips', { valueAsNumber: true })}
                  type="number"
                  className="w-full bg-base-300 text-base-content rounded-lg px-3 py-2 border border-base-300"
                />
              </div>
              <div>
                <label className="text-sm text-base-content/70 block mb-1">{t('maxCount')}</label>
                <input
                  {...register('maxAddon', { valueAsNumber: true })}
                  type="number"
                  min={0}
                  className="w-full bg-base-300 text-base-content rounded-lg px-3 py-2 border border-base-300"
                />
              </div>
            </div>
          )}
        </div>

        {error && (
          <div className="bg-red-500/20 border border-red-500/40 rounded-lg p-3 text-error text-sm">
            {error}
          </div>
        )}

        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => navigate('/tournament')}
            className="flex-1 px-4 py-3 bg-base-300 text-base-content rounded-xl hover:bg-base-300 transition-colors"
          >
            {t('cancel', { ns: 'common' })}
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="flex-1 px-4 py-3 bg-primary text-primary-content rounded-xl font-medium hover:bg-primary/90 transition-colors disabled:opacity-50"
          >
            {submitting ? t('creating') : t('create')}
          </button>
        </div>
      </form>
    </div>
  );
}
